/**
 * UI Events - DOM event wiring module
 * Centralizes event listener registration and DOM queries
 */

import {
  SAMPLE_CATEGORIES,
  CATEGORY_LABELS,
  getSamplesByCategory,
  getCategoryOrder,
  isMultiPass,
} from '../data/samples.js';

export class UIEvents {
  /**
   * @param {Object} handlers - Callback handlers from App
   * @param {Function} handlers.onCompile - Compile button click
   * @param {Function} handlers.onShare - Share button click
   * @param {Function} handlers.onOpenDownloadModal - Download button click
   * @param {Function} handlers.onScreenshot - Screenshot button click
   * @param {Function} handlers.onStartRecording - Start recording
   * @param {Function} handlers.onStopRecording - Stop recording
   * @param {Function} handlers.isRecording - Check if recording is active
   * @param {Function} handlers.onLoadTextureFile - Load texture from file
   * @param {Function} handlers.onGenerateNoise - Generate noise texture
   * @param {Function} handlers.onGenerateGradient - Generate gradient texture
   * @param {Function} handlers.onGenerateChecker - Generate checker texture
   * @param {Function} handlers.onStartCamera - Start camera on channel
   * @param {Function} handlers.onFlipCamera - Flip camera facing mode
   * @param {Function} handlers.isCameraActive - Check if camera is active
   * @param {Function} handlers.onClearTexture - Clear texture from channel
   * @param {Function} handlers.getTextureInfo - Get texture info for channel
   * @param {Function} handlers.onPassChange - Pass tab change
   * @param {Function} handlers.onAddBuffer - Add buffer button click
   * @param {Function} handlers.onDeleteBuffer - Delete buffer button click
   * @param {Function} handlers.onLoadSample - Load sample shader
   * @param {Function} handlers.onAddUniform - Add uniform
   * @param {Function} handlers.onRemoveUniform - Remove uniform
   * @param {Function} handlers.onUniformValueChange - Uniform value change
   * @param {Function} handlers.onCopyDeclaration - Copy uniform declaration
   * @param {Function} handlers.getUniformManager - Get uniform manager
   */
  constructor(handlers) {
    this.handlers = handlers;
    this.elements = {};
    this.boundHandlers = {};
    this.currentTextureChannel = 0;
    this.currentSampleCategory = SAMPLE_CATEGORIES.BASIC;
    this.pendingSampleId = null;
    this.pendingConfirmCallback = null;
  }

  init() {
    this.cacheElements();
    this.attachListeners();
  }

  cacheElements() {
    this.elements = {
      // Floating toolbar buttons
      playPauseBtn: document.getElementById('btn-playpause'),
      settingsBtn: document.getElementById('btn-settings'),
      samplesBtn: document.getElementById('btn-samples'),
      // Settings modal
      settingsModal: document.getElementById('settings-modal'),
      closeSettingsBtn: document.getElementById('btn-close-settings'),
      shareBtn: document.getElementById('btn-share'),
      downloadBtn: document.getElementById('btn-download'),
      // Download modal
      modal: document.getElementById('download-modal'),
      closeBtn: document.getElementById('btn-close-modal'),
      // Help modal
      helpModal: document.getElementById('help-modal'),
      closeHelpBtn: document.getElementById('btn-close-help'),
      screenshotBtn: document.getElementById('btn-screenshot'),
      recordBtn: document.getElementById('btn-record'),
      framesInput: document.getElementById('input-frames'),
      recordStatus: document.getElementById('record-status'),
      // Pass tabs
      passTabs: document.getElementById('pass-tabs'),
      addBufferBtn: document.getElementById('btn-add-buffer'),
      // Texture slots
      textureSlot0: document.getElementById('texture-slot-0'),
      textureSlot1: document.getElementById('texture-slot-1'),
      textureSlot2: document.getElementById('texture-slot-2'),
      textureSlot3: document.getElementById('texture-slot-3'),
      // Texture modal
      textureModal: document.getElementById('texture-modal'),
      textureModalChannel: document.getElementById('texture-modal-channel'),
      closeTextureBtn: document.getElementById('btn-close-texture'),
      texturePreview: document.getElementById('texture-preview'),
      textureInfo: document.getElementById('texture-info'),
      textureDropzone: document.getElementById('texture-dropzone'),
      textureFileInput: document.getElementById('texture-file-input'),
      presetNoiseBtn: document.getElementById('btn-preset-noise'),
      presetGradientBtn: document.getElementById('btn-preset-gradient'),
      presetCheckerBtn: document.getElementById('btn-preset-checker'),
      cameraStartBtn: document.getElementById('btn-camera-start'),
      cameraFlipBtn: document.getElementById('btn-camera-flip'),
      clearTextureBtn: document.getElementById('btn-clear-texture'),
      // Sample modal
      sampleModal: document.getElementById('sample-modal'),
      closeSampleBtn: document.getElementById('btn-close-sample'),
      sampleCategories: document.getElementById('sample-categories'),
      sampleGrid: document.getElementById('sample-grid'),
      // Confirm modal
      confirmModal: document.getElementById('confirm-modal'),
      confirmTitle: document.getElementById('confirm-title'),
      confirmMessage: document.getElementById('confirm-message'),
      closeConfirmBtn: document.getElementById('btn-close-confirm'),
      confirmCancelBtn: document.getElementById('btn-confirm-cancel'),
      confirmOkBtn: document.getElementById('btn-confirm-ok'),
      // Uniform panel
      uniformPanel: document.getElementById('uniform-panel'),
      uniformToggleBtn: document.getElementById('btn-toggle-uniforms'),
      uniformsSettingBtn: document.getElementById('btn-toggle-uniforms-setting'),
      addUniformBtn: document.getElementById('btn-add-uniform'),
      copyDeclarationBtn: document.getElementById('btn-copy-declaration'),
      uniformList: document.getElementById('uniform-list'),
      // Add uniform modal
      addUniformModal: document.getElementById('add-uniform-modal'),
      closeAddUniformBtn: document.getElementById('btn-close-add-uniform'),
      cancelAddUniformBtn: document.getElementById('btn-cancel-add-uniform'),
      submitAddUniformBtn: document.getElementById('btn-submit-add-uniform'),
      uniformNameInput: document.getElementById('input-uniform-name'),
      uniformTypeSelect: document.getElementById('select-uniform-type'),
      uniformMinInput: document.getElementById('input-uniform-min'),
      uniformMaxInput: document.getElementById('input-uniform-max'),
      uniformRangeFields: document.getElementById('uniform-range-fields'),
      addUniformError: document.getElementById('add-uniform-error'),
    };

    // Get backdrops from modals
    if (this.elements.modal) {
      this.elements.backdrop = this.elements.modal.querySelector('.modal-backdrop');
    }
    if (this.elements.settingsModal) {
      this.elements.settingsBackdrop = this.elements.settingsModal.querySelector('.modal-backdrop');
    }
    if (this.elements.helpModal) {
      this.elements.helpBackdrop = this.elements.helpModal.querySelector('.modal-backdrop');
    }
    if (this.elements.textureModal) {
      this.elements.textureBackdrop = this.elements.textureModal.querySelector('.modal-backdrop');
    }
    if (this.elements.sampleModal) {
      this.elements.sampleBackdrop = this.elements.sampleModal.querySelector('.modal-backdrop');
    }
    if (this.elements.confirmModal) {
      this.elements.confirmBackdrop = this.elements.confirmModal.querySelector('.modal-backdrop');
    }
    if (this.elements.addUniformModal) {
      this.elements.addUniformBackdrop = this.elements.addUniformModal.querySelector('.modal-backdrop');
    }
  }

  attachListeners() {
    const { elements, handlers } = this;

    // Play/Pause button
    this.boundHandlers.togglePause = () => {
      const isPaused = handlers.onTogglePause?.();
      this.updatePlayPauseIcon(isPaused);
    };
    elements.playPauseBtn?.addEventListener('click', this.boundHandlers.togglePause);

    // Settings modal open
    this.boundHandlers.openSettings = () => this.openSettingsModal();
    elements.settingsBtn?.addEventListener('click', this.boundHandlers.openSettings);

    // Settings modal close
    this.boundHandlers.closeSettings = () => this.closeSettingsModal();
    elements.closeSettingsBtn?.addEventListener('click', this.boundHandlers.closeSettings);
    elements.settingsBackdrop?.addEventListener('click', this.boundHandlers.closeSettings);

    // Settings modal action buttons
    elements.shareBtn?.addEventListener('click', handlers.onShare);

    // Floating toolbar download button
    elements.downloadBtn?.addEventListener('click', handlers.onOpenDownloadModal);

    // Download modal close handlers
    this.boundHandlers.closeModal = () => this.closeModal();
    elements.closeBtn?.addEventListener('click', this.boundHandlers.closeModal);
    elements.backdrop?.addEventListener('click', this.boundHandlers.closeModal);

    // Help modal close handlers
    this.boundHandlers.closeHelp = () => this.closeHelpModal();
    elements.closeHelpBtn?.addEventListener('click', this.boundHandlers.closeHelp);
    elements.helpBackdrop?.addEventListener('click', this.boundHandlers.closeHelp);

    // Keyboard shortcuts
    this.boundHandlers.onKeydown = (e) => {
      const isMod = e.metaKey || e.ctrlKey;

      // Mod+key shortcuts (work even in editor)
      if (isMod) {
        switch (e.key.toLowerCase()) {
          case 's':
            e.preventDefault();
            handlers.onShare?.();
            break;
          case 'd':
            e.preventDefault();
            handlers.onOpenDownloadModal?.();
            break;
          case ',':
            e.preventDefault();
            this.toggleSettingsModal();
            break;
        }
        return;
      }

      // Ignore other keys if typing in an input or editor
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' ||
          e.target.closest('.cm-editor')) {
        return;
      }

      switch (e.key) {
        case 'Escape':
          if (!elements.addUniformModal?.classList.contains('hidden')) {
            this.closeAddUniformModal();
          } else if (!elements.confirmModal?.classList.contains('hidden')) {
            this.closeConfirmModal();
          } else if (!elements.sampleModal?.classList.contains('hidden')) {
            this.closeSampleModal();
          } else if (!elements.modal?.classList.contains('hidden')) {
            this.closeModal();
          } else if (!elements.settingsModal?.classList.contains('hidden')) {
            this.closeSettingsModal();
          } else if (!elements.helpModal?.classList.contains('hidden')) {
            this.closeHelpModal();
          } else if (!elements.textureModal?.classList.contains('hidden')) {
            this.closeTextureModal();
          } else {
            handlers.onExitFullscreen?.();
          }
          break;
        case ' ':
          e.preventDefault();
          this.boundHandlers.togglePause?.();
          break;
        case 'f':
          e.preventDefault();
          handlers.onToggleFullscreen?.();
          break;
        case 'e':
          e.preventDefault();
          this.toggleSampleModal();
          break;
        case 'u':
          e.preventDefault();
          this.toggleUniformPanel();
          break;
        case '?':
          e.preventDefault();
          this.toggleHelpModal();
          break;
        // Pass switching with number keys
        case '1':
          e.preventDefault();
          handlers.onPassChange?.('main');
          break;
        case '2':
          e.preventDefault();
          handlers.onPassChange?.('bufferA');
          break;
        case '3':
          e.preventDefault();
          handlers.onPassChange?.('bufferB');
          break;
        case '4':
          e.preventDefault();
          handlers.onPassChange?.('bufferC');
          break;
        case '5':
          e.preventDefault();
          handlers.onPassChange?.('bufferD');
          break;
      }
    };
    document.addEventListener('keydown', this.boundHandlers.onKeydown);

    // Screenshot button
    elements.screenshotBtn?.addEventListener('click', handlers.onScreenshot);

    // Record button
    this.boundHandlers.onRecordClick = () => {
      if (handlers.isRecording?.()) {
        handlers.onStopRecording?.();
      } else {
        const maxFrames = parseInt(elements.framesInput?.value, 10) || 300;
        handlers.onStartRecording?.(maxFrames);
      }
    };
    elements.recordBtn?.addEventListener('click', this.boundHandlers.onRecordClick);

    // Texture slot clicks
    this.boundHandlers.openTextureModal0 = () => this.openTextureModal(0);
    this.boundHandlers.openTextureModal1 = () => this.openTextureModal(1);
    this.boundHandlers.openTextureModal2 = () => this.openTextureModal(2);
    this.boundHandlers.openTextureModal3 = () => this.openTextureModal(3);
    elements.textureSlot0?.addEventListener('click', this.boundHandlers.openTextureModal0);
    elements.textureSlot1?.addEventListener('click', this.boundHandlers.openTextureModal1);
    elements.textureSlot2?.addEventListener('click', this.boundHandlers.openTextureModal2);
    elements.textureSlot3?.addEventListener('click', this.boundHandlers.openTextureModal3);

    // Texture modal close
    this.boundHandlers.closeTexture = () => this.closeTextureModal();
    elements.closeTextureBtn?.addEventListener('click', this.boundHandlers.closeTexture);
    elements.textureBackdrop?.addEventListener('click', this.boundHandlers.closeTexture);

    // Texture dropzone click
    this.boundHandlers.onDropzoneClick = () => elements.textureFileInput?.click();
    elements.textureDropzone?.addEventListener('click', this.boundHandlers.onDropzoneClick);

    // Texture file input change
    this.boundHandlers.onFileChange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        handlers.onLoadTextureFile?.(file, this.currentTextureChannel);
        e.target.value = ''; // Reset for same file re-upload
      }
    };
    elements.textureFileInput?.addEventListener('change', this.boundHandlers.onFileChange);

    // Drag & drop handlers
    this.boundHandlers.onDragOver = (e) => {
      e.preventDefault();
      elements.textureDropzone?.classList.add('dragover');
    };
    this.boundHandlers.onDragLeave = () => {
      elements.textureDropzone?.classList.remove('dragover');
    };
    this.boundHandlers.onDrop = (e) => {
      e.preventDefault();
      elements.textureDropzone?.classList.remove('dragover');
      const file = e.dataTransfer?.files?.[0];
      if (file) {
        handlers.onLoadTextureFile?.(file, this.currentTextureChannel);
      }
    };
    elements.textureDropzone?.addEventListener('dragover', this.boundHandlers.onDragOver);
    elements.textureDropzone?.addEventListener('dragleave', this.boundHandlers.onDragLeave);
    elements.textureDropzone?.addEventListener('drop', this.boundHandlers.onDrop);

    // Preset buttons
    this.boundHandlers.onNoisePreset = () => {
      handlers.onGenerateNoise?.(this.currentTextureChannel);
    };
    this.boundHandlers.onGradientPreset = () => {
      handlers.onGenerateGradient?.(this.currentTextureChannel);
    };
    this.boundHandlers.onCheckerPreset = () => {
      handlers.onGenerateChecker?.(this.currentTextureChannel);
    };
    elements.presetNoiseBtn?.addEventListener('click', this.boundHandlers.onNoisePreset);
    elements.presetGradientBtn?.addEventListener('click', this.boundHandlers.onGradientPreset);
    elements.presetCheckerBtn?.addEventListener('click', this.boundHandlers.onCheckerPreset);

    // Camera buttons
    this.boundHandlers.onCameraStart = () => {
      handlers.onStartCamera?.(this.currentTextureChannel);
    };
    this.boundHandlers.onCameraFlip = () => {
      handlers.onFlipCamera?.(this.currentTextureChannel);
    };
    elements.cameraStartBtn?.addEventListener('click', this.boundHandlers.onCameraStart);
    elements.cameraFlipBtn?.addEventListener('click', this.boundHandlers.onCameraFlip);

    // Clear texture button
    this.boundHandlers.onClearTexture = () => {
      handlers.onClearTexture?.(this.currentTextureChannel);
    };
    elements.clearTextureBtn?.addEventListener('click', this.boundHandlers.onClearTexture);

    // Pass tab clicks
    this.boundHandlers.onPassTabClick = (e) => {
      const tab = e.target.closest('.pass-tab');
      if (tab) {
        const passId = tab.dataset.pass;
        handlers.onPassChange?.(passId);
      }
    };
    elements.passTabs?.addEventListener('click', this.boundHandlers.onPassTabClick);

    // Add buffer button
    this.boundHandlers.onAddBuffer = () => {
      handlers.onAddBuffer?.();
    };
    elements.addBufferBtn?.addEventListener('click', this.boundHandlers.onAddBuffer);

    // Sample modal open
    this.boundHandlers.openSamples = () => this.openSampleModal();
    elements.samplesBtn?.addEventListener('click', this.boundHandlers.openSamples);

    // Sample modal close
    this.boundHandlers.closeSample = () => this.closeSampleModal();
    elements.closeSampleBtn?.addEventListener('click', this.boundHandlers.closeSample);
    elements.sampleBackdrop?.addEventListener('click', this.boundHandlers.closeSample);

    // Sample category tabs
    this.boundHandlers.onCategoryClick = (e) => {
      const tab = e.target.closest('.sample-category-tab');
      if (tab) {
        const category = tab.dataset.category;
        this.selectCategory(category);
      }
    };
    elements.sampleCategories?.addEventListener('click', this.boundHandlers.onCategoryClick);

    // Sample grid clicks
    this.boundHandlers.onSampleClick = (e) => {
      const card = e.target.closest('.sample-card');
      if (card) {
        const sampleId = card.dataset.sampleId;
        this.showConfirmDialog(sampleId);
      }
    };
    elements.sampleGrid?.addEventListener('click', this.boundHandlers.onSampleClick);

    // Confirm modal handlers
    this.boundHandlers.closeConfirm = () => this.closeConfirmModal();
    elements.closeConfirmBtn?.addEventListener('click', this.boundHandlers.closeConfirm);
    elements.confirmBackdrop?.addEventListener('click', this.boundHandlers.closeConfirm);
    elements.confirmCancelBtn?.addEventListener('click', this.boundHandlers.closeConfirm);

    this.boundHandlers.confirmOk = () => {
      if (this.pendingConfirmCallback) {
        this.pendingConfirmCallback();
        this.pendingConfirmCallback = null;
        this.closeConfirmModal();
      }
    };
    elements.confirmOkBtn?.addEventListener('click', this.boundHandlers.confirmOk);

    // Uniform panel toggle
    this.boundHandlers.toggleUniformPanel = (e) => {
      // Only toggle if clicking directly on the header, not on action buttons
      if (e.target.closest('.uniform-panel-actions')) return;
      this.toggleUniformPanel();
    };
    elements.uniformToggleBtn?.addEventListener('click', this.boundHandlers.toggleUniformPanel);

    // Uniforms setting toggle
    this.boundHandlers.toggleUniformsSetting = () => {
      const isVisible = handlers.onToggleUniformsSetting?.();
      this.updateUniformsSettingButton(isVisible);
    };
    elements.uniformsSettingBtn?.addEventListener('click', this.boundHandlers.toggleUniformsSetting);

    // Add uniform button
    this.boundHandlers.openAddUniform = (e) => {
      e.stopPropagation();
      this.openAddUniformModal();
    };
    elements.addUniformBtn?.addEventListener('click', this.boundHandlers.openAddUniform);

    // Copy declaration button
    this.boundHandlers.copyDeclaration = (e) => {
      e.stopPropagation();
      handlers.onCopyDeclaration?.();
    };
    elements.copyDeclarationBtn?.addEventListener('click', this.boundHandlers.copyDeclaration);

    // Add uniform modal close handlers
    this.boundHandlers.closeAddUniform = () => this.closeAddUniformModal();
    elements.closeAddUniformBtn?.addEventListener('click', this.boundHandlers.closeAddUniform);
    elements.cancelAddUniformBtn?.addEventListener('click', this.boundHandlers.closeAddUniform);
    elements.addUniformBackdrop?.addEventListener('click', this.boundHandlers.closeAddUniform);

    // Add uniform submit
    this.boundHandlers.submitAddUniform = () => this.submitAddUniform();
    elements.submitAddUniformBtn?.addEventListener('click', this.boundHandlers.submitAddUniform);

    // Uniform type change (show/hide range fields)
    this.boundHandlers.onUniformTypeChange = () => this.updateRangeFieldsVisibility();
    elements.uniformTypeSelect?.addEventListener('change', this.boundHandlers.onUniformTypeChange);

    // Uniform list event delegation (for delete buttons and value changes)
    this.boundHandlers.onUniformListClick = (e) => {
      const deleteBtn = e.target.closest('.uniform-item-delete');
      if (deleteBtn) {
        const name = deleteBtn.dataset.name;
        handlers.onRemoveUniform?.(name);
      }
    };
    elements.uniformList?.addEventListener('click', this.boundHandlers.onUniformListClick);

    // Uniform value change event delegation
    this.boundHandlers.onUniformChange = (e) => {
      const target = e.target;
      const uniformItem = target.closest('.uniform-item');
      if (!uniformItem) return;

      const name = uniformItem.dataset.name;
      const type = uniformItem.dataset.type;

      if (target.classList.contains('uniform-slider')) {
        handlers.onUniformValueChange?.(name, parseFloat(target.value));
        // Update display value
        const valueDisplay = uniformItem.querySelector('.uniform-slider-value');
        if (valueDisplay) {
          valueDisplay.textContent = parseFloat(target.value).toFixed(2);
        }
      } else if (target.classList.contains('uniform-checkbox')) {
        handlers.onUniformValueChange?.(name, target.checked ? 1 : 0);
      } else if (target.classList.contains('uniform-color-picker')) {
        const rgb = this.hexToRgb(target.value);
        handlers.onUniformValueChange?.(name, rgb);
        // Update hex display
        const hexDisplay = uniformItem.querySelector('.uniform-color-hex');
        if (hexDisplay) {
          hexDisplay.textContent = target.value.toUpperCase();
        }
      } else if (target.classList.contains('uniform-component')) {
        const components = uniformItem.querySelectorAll('.uniform-component');
        const values = Array.from(components).map((c) => parseFloat(c.value) || 0);
        handlers.onUniformValueChange?.(name, values);
      }
    };
    elements.uniformList?.addEventListener('input', this.boundHandlers.onUniformChange);
  }

  closeModal() {
    if (this.handlers.isRecording?.()) return;
    this.elements.modal?.classList.add('hidden');
  }

  openModal() {
    this.elements.modal?.classList.remove('hidden');
  }

  openSettingsModal() {
    this.elements.settingsModal?.classList.remove('hidden');
  }

  closeSettingsModal() {
    this.elements.settingsModal?.classList.add('hidden');
  }

  toggleSettingsModal() {
    this.elements.settingsModal?.classList.toggle('hidden');
  }

  openHelpModal() {
    this.elements.helpModal?.classList.remove('hidden');
  }

  closeHelpModal() {
    this.elements.helpModal?.classList.add('hidden');
  }

  toggleHelpModal() {
    this.elements.helpModal?.classList.toggle('hidden');
  }

  openSampleModal() {
    // Populate grid with current category
    this.populateSampleGrid(this.currentSampleCategory);
    this.elements.sampleModal?.classList.remove('hidden');
  }

  closeSampleModal() {
    this.elements.sampleModal?.classList.add('hidden');
  }

  toggleSampleModal() {
    if (this.elements.sampleModal?.classList.contains('hidden')) {
      this.openSampleModal();
    } else {
      this.closeSampleModal();
    }
  }

  /**
   * Select a category tab and update the grid
   * @param {string} category - Category ID
   */
  selectCategory(category) {
    this.currentSampleCategory = category;

    // Update active tab
    const tabs = this.elements.sampleCategories?.querySelectorAll('.sample-category-tab');
    tabs?.forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.category === category);
    });

    // Populate grid
    this.populateSampleGrid(category);
  }

  /**
   * Populate the sample grid with samples from a category
   * @param {string} category - Category ID
   */
  populateSampleGrid(category) {
    const grid = this.elements.sampleGrid;
    if (!grid) return;

    const samples = getSamplesByCategory(category);

    grid.innerHTML = samples
      .map((sample) => {
        const multiPassBadge = isMultiPass(sample)
          ? '<span class="sample-card-badge">Multi-pass</span>'
          : '';
        return `
          <div class="sample-card" data-sample-id="${sample.id}">
            ${multiPassBadge}
            <p class="sample-card-name">${sample.name}</p>
            <p class="sample-card-description">${sample.description}</p>
          </div>
        `;
      })
      .join('');
  }

  /**
   * Show generic confirm dialog
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title
   * @param {string} options.message - Dialog message
   * @param {string} options.confirmText - Confirm button text
   * @param {boolean} [options.danger] - Use danger style for confirm button
   * @param {Function} options.onConfirm - Callback when confirmed
   */
  showConfirm({ title, message, confirmText, danger = false, onConfirm }) {
    const { confirmModal, confirmTitle, confirmMessage, confirmOkBtn } = this.elements;

    if (confirmTitle) confirmTitle.textContent = title;
    if (confirmMessage) confirmMessage.textContent = message;
    if (confirmOkBtn) {
      confirmOkBtn.textContent = confirmText;
      confirmOkBtn.classList.toggle('btn-danger', danger);
      confirmOkBtn.classList.toggle('btn-primary', !danger);
    }

    this.pendingConfirmCallback = onConfirm;
    confirmModal?.classList.remove('hidden');
  }

  /**
   * Show confirm dialog for loading a sample
   * @param {string} sampleId - Sample ID
   */
  showConfirmDialog(sampleId) {
    this.pendingSampleId = sampleId;
    this.showConfirm({
      title: 'Load Sample?',
      message: 'Loading this sample will replace your current shader code.',
      confirmText: 'Load Sample',
      onConfirm: () => {
        if (this.pendingSampleId) {
          this.handlers.onLoadSample?.(this.pendingSampleId);
          this.pendingSampleId = null;
          this.closeSampleModal();
        }
      },
    });
  }

  /**
   * Show confirm dialog for deleting a buffer
   * @param {string} passId - Buffer ID
   * @param {string} label - Buffer label (A, B, C, D)
   */
  showDeleteBufferConfirm(passId, label) {
    this.showConfirm({
      title: 'Delete Buffer?',
      message: `Are you sure you want to delete Buffer ${label}? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
      onConfirm: () => {
        this.handlers.onDeleteBuffer?.(passId);
      },
    });
  }

  closeConfirmModal() {
    this.elements.confirmModal?.classList.add('hidden');
    this.pendingSampleId = null;
    this.pendingConfirmCallback = null;
  }

  // Uniform panel methods
  toggleUniformPanel() {
    this.elements.uniformPanel?.classList.toggle('collapsed');
  }

  openUniformPanel() {
    this.elements.uniformPanel?.classList.remove('collapsed');
  }

  closeUniformPanel() {
    this.elements.uniformPanel?.classList.add('collapsed');
  }

  /**
   * Set uniform panel visibility (show/hide completely)
   * @param {boolean} visible
   */
  setUniformsPanelVisible(visible) {
    this.elements.uniformPanel?.classList.toggle('hidden', !visible);
    this.updateUniformsSettingButton(visible);
  }

  /**
   * Update uniforms setting button state
   * @param {boolean} isEnabled
   */
  updateUniformsSettingButton(isEnabled) {
    const btn = this.elements.uniformsSettingBtn;
    if (btn) {
      btn.textContent = isEnabled ? 'Disable' : 'Enable';
      btn.classList.toggle('active', isEnabled);
    }
  }

  openAddUniformModal() {
    // Reset form
    const { uniformNameInput, uniformTypeSelect, uniformMinInput, uniformMaxInput, addUniformError } =
      this.elements;
    if (uniformNameInput) uniformNameInput.value = '';
    if (uniformTypeSelect) uniformTypeSelect.value = 'float';
    if (uniformMinInput) uniformMinInput.value = '0';
    if (uniformMaxInput) uniformMaxInput.value = '1';
    if (addUniformError) addUniformError.classList.add('hidden');
    this.updateRangeFieldsVisibility();

    this.elements.addUniformModal?.classList.remove('hidden');
    uniformNameInput?.focus();
  }

  closeAddUniformModal() {
    this.elements.addUniformModal?.classList.add('hidden');
  }

  updateRangeFieldsVisibility() {
    const type = this.elements.uniformTypeSelect?.value;
    const show = type === 'float' || type === 'bool';
    this.elements.uniformRangeFields?.classList.toggle('hidden', !show);
  }

  submitAddUniform() {
    const { uniformNameInput, uniformTypeSelect, uniformMinInput, uniformMaxInput, addUniformError } =
      this.elements;

    const name = uniformNameInput?.value?.trim() || '';
    const type = uniformTypeSelect?.value || 'float';
    const min = parseFloat(uniformMinInput?.value) || 0;
    const max = parseFloat(uniformMaxInput?.value) || 1;

    const result = this.handlers.onAddUniform?.({
      name,
      type,
      min,
      max,
      step: type === 'bool' ? 1 : 0.01,
    });

    if (result && !result.success) {
      if (addUniformError) {
        addUniformError.textContent = result.error;
        addUniformError.classList.remove('hidden');
      }
    } else {
      this.closeAddUniformModal();
      this.openUniformPanel();
    }
  }

  /**
   * Refresh uniform list UI
   */
  refreshUniformList() {
    const manager = this.handlers.getUniformManager?.();
    if (!manager) return;

    const list = this.elements.uniformList;
    if (!list) return;

    const uniforms = manager.getAll();

    if (uniforms.length === 0) {
      list.innerHTML = '<p class="uniform-empty-hint">No custom uniforms.<br>Click + to add one.</p>';
      return;
    }

    list.innerHTML = uniforms.map((uniform) => this.renderUniformControl(uniform)).join('');
  }

  /**
   * Render HTML for a single uniform control
   * @param {Object} uniform
   * @returns {string}
   */
  renderUniformControl(uniform) {
    const { name, type, value, min, max, step } = uniform;

    let controlHtml = '';

    switch (type) {
      case 'float':
        controlHtml = `
          <div class="uniform-slider-row">
            <input type="range" class="uniform-slider"
              value="${value}" min="${min}" max="${max}" step="${step}">
            <span class="uniform-slider-value">${Number(value).toFixed(2)}</span>
          </div>`;
        break;

      case 'bool':
        controlHtml = `
          <div class="uniform-bool-row">
            <input type="checkbox" class="uniform-checkbox" ${value ? 'checked' : ''}>
            <span class="uniform-bool-label">${value ? 'true' : 'false'}</span>
          </div>`;
        break;

      case 'color':
        const hexColor = this.rgbToHex(value);
        controlHtml = `
          <div class="uniform-color-row">
            <input type="color" class="uniform-color-picker" value="${hexColor}">
            <span class="uniform-color-hex">${hexColor.toUpperCase()}</span>
          </div>`;
        break;

      case 'vec2':
        controlHtml = `
          <div class="uniform-vector-row">
            ${this.renderVectorInputs(value, ['X', 'Y'])}
          </div>`;
        break;

      case 'vec3':
        controlHtml = `
          <div class="uniform-vector-row">
            ${this.renderVectorInputs(value, ['X', 'Y', 'Z'])}
          </div>`;
        break;

      case 'vec4':
        controlHtml = `
          <div class="uniform-vector-row">
            ${this.renderVectorInputs(value, ['X', 'Y', 'Z', 'W'])}
          </div>`;
        break;
    }

    return `
      <div class="uniform-item" data-name="${name}" data-type="${type}">
        <div class="uniform-item-header">
          <div>
            <span class="uniform-item-name">${name}</span>
            <span class="uniform-item-type">${type}</span>
          </div>
          <button class="uniform-item-delete" data-name="${name}" title="Remove">×</button>
        </div>
        ${controlHtml}
      </div>`;
  }

  /**
   * Render vector component inputs
   * @param {number[]} values
   * @param {string[]} labels
   * @returns {string}
   */
  renderVectorInputs(values, labels) {
    return labels
      .map(
        (label, i) => `
      <div class="uniform-vector-input">
        <label>${label}</label>
        <input type="number" class="uniform-component" value="${values[i] || 0}" step="0.01">
      </div>`
      )
      .join('');
  }

  /**
   * Convert RGB array [0-1] to hex string
   * @param {number[]} rgb
   * @returns {string}
   */
  rgbToHex(rgb) {
    const r = Math.round((rgb[0] || 0) * 255);
    const g = Math.round((rgb[1] || 0) * 255);
    const b = Math.round((rgb[2] || 0) * 255);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  /**
   * Convert hex string to RGB array [0-1]
   * @param {string} hex
   * @returns {number[]}
   */
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return [1, 1, 1];
    return [parseInt(result[1], 16) / 255, parseInt(result[2], 16) / 255, parseInt(result[3], 16) / 255];
  }

  /**
   * Show button feedback for copy declaration
   * @param {string} text
   */
  showCopyFeedback(text) {
    const btn = this.elements.copyDeclarationBtn;
    if (!btn) return;

    const originalHtml = btn.innerHTML;
    btn.innerHTML = text;
    setTimeout(() => {
      btn.innerHTML = originalHtml;
    }, 1500);
  }

  openTextureModal(channel) {
    this.currentTextureChannel = channel;
    const { textureModal, textureModalChannel } = this.elements;

    if (textureModalChannel) {
      textureModalChannel.textContent = `iChannel${channel}`;
    }

    // Update preview with current texture info
    this.updateTexturePreview(channel);

    // Update camera button state
    const isCameraActive = this.handlers.isCameraActive?.(channel) ?? false;
    this.updateCameraButtonState(isCameraActive);

    textureModal?.classList.remove('hidden');
  }

  closeTextureModal() {
    this.elements.textureModal?.classList.add('hidden');
  }

  /**
   * Update texture slot thumbnail
   * @param {number} channel
   * @param {Object|null} info - Texture info or null if cleared
   * @param {string} [thumbnailUrl] - Data URL for thumbnail
   */
  updateTextureSlot(channel, info, thumbnailUrl) {
    const slot = this.elements[`textureSlot${channel}`];
    if (!slot) return;

    if (info && thumbnailUrl) {
      slot.classList.add('has-texture');
      slot.style.backgroundImage = `url(${thumbnailUrl})`;
    } else {
      slot.classList.remove('has-texture');
      slot.style.backgroundImage = '';
    }
  }

  /**
   * Update texture preview in modal
   * @param {number} channel
   */
  updateTexturePreview(channel) {
    const { texturePreview, textureInfo } = this.elements;
    const info = this.handlers.getTextureInfo?.(channel);

    if (info) {
      texturePreview?.classList.add('has-texture');
      if (textureInfo) {
        textureInfo.textContent = `${info.width} × ${info.height}\n${info.source}`;
      }
    } else {
      texturePreview?.classList.remove('has-texture');
      texturePreview.style.backgroundImage = '';
      if (textureInfo) {
        textureInfo.textContent = '';
      }
    }
  }

  /**
   * Set texture preview image
   * @param {string} dataUrl
   */
  setTexturePreviewImage(dataUrl) {
    const { texturePreview } = this.elements;
    if (texturePreview) {
      texturePreview.classList.add('has-texture');
      texturePreview.style.backgroundImage = `url(${dataUrl})`;
    }
  }

  /**
   * Update camera button state
   * @param {boolean} isActive
   */
  updateCameraButtonState(isActive) {
    const { cameraStartBtn, cameraFlipBtn } = this.elements;
    if (cameraStartBtn) {
      cameraStartBtn.textContent = isActive ? 'Stop Camera' : 'Start Camera';
    }
    if (cameraFlipBtn) {
      cameraFlipBtn.disabled = !isActive;
    }
  }

  /**
   * Set active pass tab
   * @param {string} passId - Pass ID
   */
  setActivePassTab(passId) {
    const tabs = this.elements.passTabs?.querySelectorAll('.pass-tab');
    tabs?.forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.pass === passId);
    });
  }

  /**
   * Set error state on pass tab
   * @param {string} passId - Pass ID
   * @param {boolean} hasError
   */
  setPassTabError(passId, hasError) {
    const tab = this.elements.passTabs?.querySelector(`[data-pass="${passId}"]`);
    if (tab) {
      tab.classList.toggle('has-error', hasError);
    }
  }

  /**
   * Add a new buffer tab
   * @param {string} passId - Buffer ID (e.g., 'bufferB')
   * @param {string} label - Tab label (e.g., 'B')
   */
  addBufferTab(passId, label) {
    const { passTabs } = this.elements;
    if (!passTabs) return;

    // Create new tab button
    const tab = document.createElement('button');
    tab.className = 'pass-tab';
    tab.dataset.pass = passId;

    // Create label span
    const labelSpan = document.createElement('span');
    labelSpan.className = 'pass-tab-label';
    labelSpan.textContent = label;
    tab.appendChild(labelSpan);

    // Create delete button
    const deleteBtn = document.createElement('span');
    deleteBtn.className = 'pass-tab-delete';
    deleteBtn.textContent = '×';
    deleteBtn.title = 'Delete buffer';
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent tab switch
      this.showDeleteBufferConfirm(passId, label);
    });
    tab.appendChild(deleteBtn);

    // Append at end (add button is first, then Main, then buffer tabs)
    passTabs.appendChild(tab);
  }

  /**
   * Remove a buffer tab
   * @param {string} passId - Buffer ID to remove
   */
  removeBufferTab(passId) {
    const { passTabs } = this.elements;
    if (!passTabs) return;

    const tab = passTabs.querySelector(`[data-pass="${passId}"]`);
    if (tab) {
      tab.remove();
    }
  }

  /**
   * Update add buffer button visibility
   * @param {boolean} visible
   */
  setAddBufferButtonVisible(visible) {
    this.elements.addBufferBtn?.classList.toggle('hidden', !visible);
  }

  updatePlayPauseIcon(isPaused) {
    const btn = this.elements.playPauseBtn;
    if (!btn) return;

    const pauseIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="6" y="4" width="4" height="16"/>
      <rect x="14" y="4" width="4" height="16"/>
    </svg>`;

    const playIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="5,3 19,12 5,21"/>
    </svg>`;

    btn.innerHTML = isPaused ? playIcon : pauseIcon;
    btn.title = isPaused ? 'Play (Space)' : 'Pause (Space)';
  }

  // UI update methods for recording state
  setRecordingState(isRecording, maxFrames = 0) {
    const { recordBtn, recordStatus } = this.elements;
    if (isRecording) {
      recordBtn.textContent = 'Stop Recording';
      recordBtn.classList.add('recording');
      recordStatus?.classList.remove('hidden');
      recordStatus.textContent = `0/${maxFrames}`;
    } else {
      recordBtn.textContent = 'Start Recording';
      recordBtn.classList.remove('recording');
      recordStatus?.classList.add('hidden');
    }
  }

  updateRecordingProgress(current, max) {
    const { recordStatus } = this.elements;
    if (recordStatus) {
      recordStatus.textContent = `${current}/${max}`;
    }
  }

  // Button feedback helpers
  showButtonFeedback(buttonId, feedbackText, duration = 1500) {
    const btn = document.getElementById(buttonId);
    if (!btn) return;

    const originalText = btn.textContent;
    btn.textContent = feedbackText;
    setTimeout(() => {
      btn.textContent = originalText;
    }, duration);
  }

  destroy() {
    const { elements, handlers, boundHandlers } = this;

    // Floating toolbar buttons
    elements.playPauseBtn?.removeEventListener('click', boundHandlers.togglePause);
    elements.settingsBtn?.removeEventListener('click', boundHandlers.openSettings);

    // Settings modal handlers
    elements.closeSettingsBtn?.removeEventListener('click', boundHandlers.closeSettings);
    elements.settingsBackdrop?.removeEventListener('click', boundHandlers.closeSettings);
    elements.shareBtn?.removeEventListener('click', handlers.onShare);

    // Download modal handlers
    elements.closeBtn?.removeEventListener('click', boundHandlers.closeModal);
    elements.backdrop?.removeEventListener('click', boundHandlers.closeModal);

    // Help modal handlers
    elements.closeHelpBtn?.removeEventListener('click', boundHandlers.closeHelp);
    elements.helpBackdrop?.removeEventListener('click', boundHandlers.closeHelp);

    // Texture modal handlers
    elements.textureSlot0?.removeEventListener('click', boundHandlers.openTextureModal0);
    elements.textureSlot1?.removeEventListener('click', boundHandlers.openTextureModal1);
    elements.textureSlot2?.removeEventListener('click', boundHandlers.openTextureModal2);
    elements.textureSlot3?.removeEventListener('click', boundHandlers.openTextureModal3);
    elements.closeTextureBtn?.removeEventListener('click', boundHandlers.closeTexture);
    elements.textureBackdrop?.removeEventListener('click', boundHandlers.closeTexture);
    elements.textureDropzone?.removeEventListener('click', boundHandlers.onDropzoneClick);
    elements.textureFileInput?.removeEventListener('change', boundHandlers.onFileChange);
    elements.textureDropzone?.removeEventListener('dragover', boundHandlers.onDragOver);
    elements.textureDropzone?.removeEventListener('dragleave', boundHandlers.onDragLeave);
    elements.textureDropzone?.removeEventListener('drop', boundHandlers.onDrop);
    elements.presetNoiseBtn?.removeEventListener('click', boundHandlers.onNoisePreset);
    elements.presetGradientBtn?.removeEventListener('click', boundHandlers.onGradientPreset);
    elements.presetCheckerBtn?.removeEventListener('click', boundHandlers.onCheckerPreset);
    elements.cameraStartBtn?.removeEventListener('click', boundHandlers.onCameraStart);
    elements.cameraFlipBtn?.removeEventListener('click', boundHandlers.onCameraFlip);
    elements.clearTextureBtn?.removeEventListener('click', boundHandlers.onClearTexture);

    // Pass tabs
    elements.passTabs?.removeEventListener('click', boundHandlers.onPassTabClick);
    elements.addBufferBtn?.removeEventListener('click', boundHandlers.onAddBuffer);

    // Sample modal handlers
    elements.samplesBtn?.removeEventListener('click', boundHandlers.openSamples);
    elements.closeSampleBtn?.removeEventListener('click', boundHandlers.closeSample);
    elements.sampleBackdrop?.removeEventListener('click', boundHandlers.closeSample);
    elements.sampleCategories?.removeEventListener('click', boundHandlers.onCategoryClick);
    elements.sampleGrid?.removeEventListener('click', boundHandlers.onSampleClick);

    // Confirm modal handlers
    elements.closeConfirmBtn?.removeEventListener('click', boundHandlers.closeConfirm);
    elements.confirmBackdrop?.removeEventListener('click', boundHandlers.closeConfirm);
    elements.confirmCancelBtn?.removeEventListener('click', boundHandlers.closeConfirm);
    elements.confirmOkBtn?.removeEventListener('click', boundHandlers.confirmOk);

    document.removeEventListener('keydown', boundHandlers.onKeydown);

    // Capture buttons
    elements.screenshotBtn?.removeEventListener('click', handlers.onScreenshot);
    elements.recordBtn?.removeEventListener('click', boundHandlers.onRecordClick);

    // Uniform panel handlers
    elements.uniformToggleBtn?.removeEventListener('click', boundHandlers.toggleUniformPanel);
    elements.uniformsSettingBtn?.removeEventListener('click', boundHandlers.toggleUniformsSetting);
    elements.addUniformBtn?.removeEventListener('click', boundHandlers.openAddUniform);
    elements.copyDeclarationBtn?.removeEventListener('click', boundHandlers.copyDeclaration);
    elements.closeAddUniformBtn?.removeEventListener('click', boundHandlers.closeAddUniform);
    elements.cancelAddUniformBtn?.removeEventListener('click', boundHandlers.closeAddUniform);
    elements.addUniformBackdrop?.removeEventListener('click', boundHandlers.closeAddUniform);
    elements.submitAddUniformBtn?.removeEventListener('click', boundHandlers.submitAddUniform);
    elements.uniformTypeSelect?.removeEventListener('change', boundHandlers.onUniformTypeChange);
    elements.uniformList?.removeEventListener('click', boundHandlers.onUniformListClick);
    elements.uniformList?.removeEventListener('input', boundHandlers.onUniformChange);

    this.elements = {};
    this.boundHandlers = {};
  }
}
